import { Device } from '@awesome-cordova-plugins/device';
import { Capacitor } from '@capacitor/core';
import { AnyAction, createAsyncThunk, createSlice, PayloadAction, ThunkDispatch } from '@reduxjs/toolkit';
import { debounce } from 'lodash';
import moment from 'moment';
import { BehaviorSubject, filter, firstValueFrom, take } from 'rxjs';

import { RootState } from '.';
import { sendEmail } from '../api/dms/email.api';
import { logConsole, logTransaction } from '../common/log';
import LZString from '../common/lzStrtingUtil';
import { clearLocationsLog, getAccurateLocations, getLocations, getLocationsLog } from '../common/mapsUtil';
import { handleThunkError } from '../common/util';
import { IEmailMessage } from '../models/dms/email.model';
import { IGeolocation } from '../models/geolocation.model';
import { ITransactionLog, LogType } from '../models/log.model';
import { ConfigName, deleteConfig, selectConfigString, updateConfigInteger, updateConfigString } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';

interface ILogState {
  transactionLogSize: number;
}

const initialState: ILogState = {
  transactionLogSize: 0,
};

// Manage concurrent SQL queries
let runQuerySubject: BehaviorSubject<any> = new BehaviorSubject<number>(0);
let runQueryNumber: number = 0;
let runQueriesInQueue: number = 0;
let logCache = [] as ITransactionLog[];

const delayedLog = debounce((s: RootState, d: ThunkDispatch<unknown, unknown, AnyAction>) => applyLog(s, d), 500);
const applyLog = async (state: RootState, dispatch: ThunkDispatch<unknown, unknown, AnyAction>) => {
  // Only run one log batch at a time
  if (runQueriesInQueue === 0) {
    runQueryNumber = 0;
    runQuerySubject.next(0);
  }

  runQueriesInQueue++;
  const nextRunQueryNumber = ++runQueryNumber;

  try {
    // Only queue if more than 1 concurrent requests
    if (runQueriesInQueue >= 2) {
      await firstValueFrom(
        runQuerySubject.pipe(
          filter((res) => res === nextRunQueryNumber - 1),
          take(1),
        ),
      );
    }

    if (logCache.length) {
      const newLogs = [...logCache];
      logCache = [];

      let transactionLog = await selectTransactionLog(state);

      newLogs.forEach((payload) => {
        const newTransactionLog = {
          ...payload,
          id: transactionLog.length,
          dt: payload.dt || new Date().getTime(),
        } as ITransactionLog;

        transactionLog.push(newTransactionLog);
      });

      // Start the process of splitting the log into multiple pieces

      const oldLogSize = state.log.transactionLogSize;
      const maxLength = 1048576;
      const transLogString = JSON.stringify(transactionLog);
      const actualLength = new Blob([transLogString]).size;
      const newLogSize = Math.round(actualLength / maxLength + 0.5);

      // There's no need to update all the log splits, just update the most recent
      // split with the most recent data

      const sizeDiff = newLogSize - oldLogSize;
      const startIndex = Math.max(newLogSize - sizeDiff - 1, 0);
      for (let splitIndex = startIndex; splitIndex < newLogSize; splitIndex++) {
        const splitPosition1 = splitIndex * maxLength;
        const splitPosition2 = splitPosition1 + maxLength;
        const newStr = transLogString.substring(splitPosition1, splitPosition2);
        await updateConfigString(`${ConfigName.TransactionLog}${splitIndex}`, newStr, true);
      }

      if (newLogSize < oldLogSize) {
        // If the log size has decreased, delete future split log data
        for (let splitIndex = newLogSize; splitIndex < oldLogSize; splitIndex++) {
          await deleteConfig(`${ConfigName.TransactionLog}${splitIndex}`, true);
        }
      }

      if (newLogSize !== oldLogSize) {
        dispatch(setTransactionLogSizeState(newLogSize));
        await updateConfigInteger(ConfigName.TransactionLogSize, newLogSize, true);
      }
    }
  } catch (ex: any) {
    throw ex;
  } finally {
    runQueriesInQueue--;
    runQuerySubject.next(nextRunQueryNumber);
  }
};

/// THUNKS

export const addTransactionLogStoreAsync = createAsyncThunk<{}, ITransactionLog>('log/addTransactionLogStoreAsync', async (payload, { rejectWithValue, dispatch, getState }) => {
  try {
    logCache.push(payload);
    await delayedLog(getState() as RootState, dispatch);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const clearTransactionLogStoreAsync = createAsyncThunk<{}, void>('log/clearTransactionLogStoreAsync', async (nil, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Clearing transaction log cache`, '[log] clearTransactionLogStoreAsync');
  try {
    const logSize = (getState() as RootState).log.transactionLogSize;
    for (let splitIndex = 0; splitIndex < logSize; splitIndex++) {
      await deleteConfig(`${ConfigName.TransactionLog}${splitIndex}`, true);
    }
    dispatch(setTransactionLogSizeState(0));
    await updateConfigInteger(ConfigName.TransactionLogSize, 0, true);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const sendDebugLogEmailAsync = createAsyncThunk<{}, void>('log/sendDebugLogEmail', async (nil, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Sending debug log via email`, '[log] sendDebugLogEmail');

  const logTypeString = (logType: LogType) => {
    switch (logType) {
      case LogType.Error:
        return '|ERROR|';
      case LogType.Info:
        return '|INFO |';
      case LogType.Warn:
        return '|WARN |';
      case LogType.Internal:
        return '|INTER|';
    }
  };

  try {
    setIsLoadingCountState(1);
    const state = getState() as RootState;

    const { selectedCompany } = state.company;
    const { selectedDatabase } = state.database;
    const { token, technician } = state.auth;
    const { lastPosition } = state.location;
    const { enableGeolocationLog } = state.settings;

    const transactionLog = await selectTransactionLog(state);

    // Get location data
    const locationsRaw = await getLocations(false, true);
    let locationsAccuate = [] as IGeolocation[];
    if (locationsRaw.length) {
      try {
        locationsAccuate = await getAccurateLocations(locationsRaw);
      } catch (ex: any) {
        logTransaction(LogType.Error, `Caught error getting accurate locations - error: ${ex?.message}`);
      }
    }

    // Convert log data to readable format
    const transactionLogString = transactionLog.map((log) => `${moment(log.dt).format('YYYY-MM-DDTHH:mm:ss')}${logTypeString(log.typ)}${log.msg}`);

    // Get background geolocation log if enabled
    const backgroundLocationLog = enableGeolocationLog ? await getLocationsLog() : 'log disabled';

    const logData = {
      transactionLog: transactionLogString,
      backgroundGeolocation: backgroundLocationLog,
      locationData: {
        lastPosition,
        locationsRaw,
        locationsAccuate,
      },
    } as any;

    const logJson = JSON.stringify(logData);
    const logJsonCompressed = LZString.compressToBase64(logJson);

    const htmlContent = `
        <table style="width: 100%">
          <tr>
            <td>Version</td>
            <td>${process.env.REACT_APP_VERSION}</td>
          </tr>
          <tr>
            <td>Platform</td>
            <td>${Capacitor.getPlatform()}</td>
          </tr>
          <tr>
            <td>Manufacturer</td>
            <td>${Device.manufacturer}</td>
          </tr>
          <tr>
            <td>Model</td>
            <td>${Device.model}</td>
          </tr>
          <tr>
            <td>Version</td>
            <td>${Device.version}</td>
          </tr>
          <tr>
            <td>Company</td>
            <td>${selectedCompany?.name1}</td>
          </tr>
          <tr>
            <td>Database</td>
            <td>${selectedDatabase?.dbDisplayName}</td>
          </tr>
          <tr>
            <td>Username</td>
            <td>${token?.userName}</td>
          </tr>
          <tr>
            <td>Technician</td>
            <td>${technician?.displayName} (${technician?.code})</td>
          </tr>
          <tr>
            <td colspan="2">Data</td>
          </tr>
          <tr>
            <td colspan="2">${logJsonCompressed}</td>
          </tr>
          <tr>
            <td colspan="2"><a href="https://lzstring.azurewebsites.net/Decompress">Decompress</a></td>
          </tr>
        </table>
      `;

    await sendEmail({
      subject: 'Tech Mobile Support Log',
      from: {
        address: 'autoit.rd@gmail.com',
      },
      tos: [
        {
          address: 'lgray@auto-it.com',
        },
      ],
      htmlContent: htmlContent,
    } as IEmailMessage);

    await dispatch(clearTransactionLogStoreAsync());
    await clearLocationsLog();
    setIsLoadingCountState(-1);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
  }
});

/// SLICE

export const logSlice = createSlice({
  name: 'log',
  initialState,
  reducers: {
    setTransactionLogSizeState: (state, action: PayloadAction<number>) => {
      if (state.transactionLogSize !== action.payload) {
        logConsole(LogType.Info, '[log] setTransactionLogSizeState');
        state.transactionLogSize = action.payload || 0;
      }
    },
  },
});

/// REDUCERS

export const { setTransactionLogSizeState } = logSlice.actions;

/// CUSTOM SELECTORS
/*
 ** NOTE:
 ** To use this in a React component: selectTemplate(store.getState())
 */

export const selectTransactionLog = async (state: RootState): Promise<ITransactionLog[]> => {
  let logString = '';
  for (let logIndex = 0; logIndex < state.log.transactionLogSize; logIndex++) {
    const logData = await selectConfigString(`${ConfigName.TransactionLog}${logIndex}`, true);
    logString += logData;
  }

  let transactionLog = [] as ITransactionLog[];
  try {
    transactionLog = JSON.parse(logString || '[]') as ITransactionLog[];
  } catch (ex: any) {
    transactionLog.push({
      id: 0,
      dt: new Date().getTime(),
      msg: `Failed to select transaction log. ${ex.message}`,
      typ: LogType.Error,
    });
  }

  transactionLog = transactionLog.filter((t) => {
    const transDate = moment(t.dt);
    const dateDiff = moment().diff(transDate, 'hours');
    return dateDiff <= 24;
  });

  return transactionLog;
};

export default logSlice.reducer;
